import { useState, useMemo } from "react";
import { Shirt } from "lucide-react";
import { toast } from "sonner";
import { useSizing } from "@/lib/sizing-store";
import { buildFitOptions } from "@/lib/fit-copy";
import { AssignmentDialogShell, AssignOptionRow } from "./AssignmentDialogShell";

/**
 * ProductFitDialog — Kalıp bilgisi picker. Feeds the Find-My-Size widget.
 * Built on the shared AssignmentDialogShell so it shares the same
 * header/body/footer skeleton as the Model + Görsel dialogs. Clicking a
 * fit option assigns it immediately (no separate Kaydet button).
 *
 * Every product carries a default "Normal kalıp", therefore the dialog
 * intentionally does NOT expose a "Bağlantıyı kaldır" affordance — an unset
 * fit isn't a state the merchant needs.
 */
export const ProductFitDialog = ({ product, onClose }) => {
    const { setProductFit, fitLabels } = useSizing();
    const [saving, setSaving] = useState(false);
    // Tenant-customized copy for the fit rows. Falls back to defaults if
    // fitLabels not hydrated yet (first render). Recomputes when tenant edits.
    const fitOptions = useMemo(() => buildFitOptions(fitLabels), [fitLabels]);
    const currentKey = product.fit || null;

    const apply = async (key) => {
        if (saving) return;
        setSaving(true);
        try {
            await setProductFit(product.id, key);
            toast.success(key ? `Kalıp: ${(fitOptions.find((o) => o.storedAs === key)?.label) || key}` : "Kalıp bilgisi temizlendi");
            onClose();
        } catch {
            toast.error("Kalıp güncellenemedi");
        } finally {
            setSaving(false);
        }
    };

    return (
        <AssignmentDialogShell
            title="Kalıp bilgisi"
            product={product}
            onClose={onClose}
            testId="product-fit-dialog"
        >
            {fitOptions.length === 0 ? (
                <div className="text-center py-10 text-xs text-muted-foreground">
                    Kalıp tanımı yok.{" "}
                    <a href="/app/sizing/fit-labels" target="_blank" rel="noopener" className="underline hover:text-foreground">
                        Kalıp Bilgileri sayfasından
                    </a>{" "}
                    özelleştir.
                </div>
            ) : (
                fitOptions.map((o) => (
                    <AssignOptionRow
                        key={o.storedAs}
                        current={currentKey === o.storedAs}
                        onClick={() => apply(o.storedAs)}
                        disabled={saving}
                        testId={`fit-option-${o.storedAs}`}
                    >
                        <div className="h-10 w-10 rounded-md border border-border bg-muted grid place-items-center shrink-0">
                            <Shirt className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{o.label}</div>
                            {o.advice && (
                                <div className="text-[10.5px] text-muted-foreground truncate">{o.advice}</div>
                            )}
                        </div>
                    </AssignOptionRow>
                ))
            )}
        </AssignmentDialogShell>
    );
};
